<template>
  <v-form class="enterprise-form">
    <div class="form-intro">
      <p class="intro-eyebrow">Enterprise inquiry</p>
      <p class="intro-title">Tell us what you need in under 2 minutes.</p>
      <p class="intro-subtitle">
        We reply within 1 business day. No commitment required.
      </p>
    </div>

    <p class="section-label">Contact details</p>
    <v-row dense>
      <v-col cols="12" md="6">
        <v-text-field
          :value="value.name"
          label="Full name"
          placeholder="Jane Doe"
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('name', $event)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          :value="value.work_email"
          label="Work email"
          type="email"
          placeholder="you@hospital.com"
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('work_email', $event)"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="7">
        <v-text-field
          :value="value.organization"
          label="Organization"
          placeholder="Hospital, clinic, or company"
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('organization', $event)"
        />
      </v-col>
      <v-col cols="12" md="5">
        <v-text-field
          :value="value.role"
          label="Role"
          placeholder="Radiologist, CTO, IT lead..."
          outlined
          dense
          required
          hide-details="auto"
          @input="updateField('role', $event)"
        />
      </v-col>
    </v-row>

    <v-text-field
      :value="value.country"
      label="Country"
      placeholder="United States"
      outlined
      dense
      required
      hide-details="auto"
      @input="updateField('country', $event)"
    />

    <p class="section-label">Needs</p>
    <div class="choice-block">
      <p class="choice-label">Deployment:</p>
      <v-radio-group
        :value="deploymentSelection"
        class="choice-radio-group"
        row
        required
        hide-details="auto"
        @change="updateDeploymentType"
      >
        <v-radio
          v-for="option in deploymentTypeOptions"
          :key="option"
          :label="option"
          :value="option"
          class="choice-radio"
        />
      </v-radio-group>
    </div>

    <div class="choice-block">
      <p class="choice-label">Users (approx.):</p>
      <v-radio-group
        :value="value.estimated_users"
        class="choice-radio-group"
        row
        required
        hide-details="auto"
        @change="updateField('estimated_users', $event)"
      >
        <v-radio
          v-for="option in estimatedUsersOptions"
          :key="`users-${option}`"
          :label="option"
          :value="option"
          class="choice-radio"
        />
      </v-radio-group>
    </div>

    <div class="choice-block">
      <p class="choice-label">Storage (approx.):</p>
      <v-radio-group
        :value="value.estimated_storage"
        class="choice-radio-group"
        row
        required
        hide-details="auto"
        @change="updateField('estimated_storage', $event)"
      >
        <v-radio
          v-for="option in estimatedStorageOptions"
          :key="`storage-${option}`"
          :label="option"
          :value="option"
          class="choice-radio"
        />
      </v-radio-group>
    </div>

    <v-textarea
      :value="value.message"
      label="Anything else? (optional)"
      placeholder="Current tools, compliance needs, timeline, or budget range"
      outlined
      dense
      rows="2"
      auto-grow
      hide-details="auto"
      @input="updateField('message', $event)"
    />
    <p class="trust-note">
      We use this information only to prepare your demo and quote.
    </p>
  </v-form>
</template>

<script>
export default {
  name: "EnterpriseInquiryForm",
  props: {
    value: {
      type: Object,
      required: true
    },
    deploymentTypeOptions: {
      type: Array,
      default: () => []
    },
    estimatedStorageOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      estimatedUsersOptions: ["1-10", "11-25", "26-100", "101-500", "500+"]
    };
  },
  computed: {
    deploymentSelection() {
      const values = Array.isArray(this.value.deployment_type)
        ? this.value.deployment_type
        : [];
      return values[0] || "";
    }
  },
  methods: {
    updateField(field, fieldValue) {
      this.$emit("input", {
        ...this.value,
        [field]: fieldValue
      });
    },
    updateDeploymentType(selectedValue) {
      this.updateField("deployment_type", selectedValue ? [selectedValue] : []);
    }
  }
};
</script>

<style scoped>
.enterprise-form {
  display: grid;
  gap: 12px;
  padding: 18px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(165deg, #202020, #1a1a1a 58%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.form-intro {
  margin-bottom: 4px;
}

.intro-eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b8c2cf;
}

.intro-title {
  margin: 4px 0 0;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.25;
  color: #ffffff;
}

.intro-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: #c3ccd7;
}

.section-label {
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #a6b0bc;
}

.choice-block {
  margin-top: 0;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 9px 10px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #242424;
}

.choice-label {
  margin: 4px 0 0;
  min-width: 150px;
  font-size: 12px;
  font-weight: 600;
  color: #d5dee8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.choice-radio-group {
  margin: 0;
  flex: 1;
}

::v-deep .enterprise-form .v-input {
  margin-top: 0;
}

::v-deep .enterprise-form .v-input__slot {
  border-radius: 12px !important;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;
}

::v-deep .enterprise-form .v-text-field--outlined,
::v-deep .enterprise-form .v-text-field--solo {
  border-radius: 12px !important;
  overflow: hidden;
}

::v-deep
  .enterprise-form
  .v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  background: #262626 !important;
  border-radius: 12px !important;
}

::v-deep .enterprise-form .v-text-field fieldset {
  border-color: #3f3f3f !important;
  border-radius: 12px !important;
}

::v-deep .enterprise-form .v-input.v-input--is-focused .v-input__slot {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.12);
}

::v-deep .enterprise-form .v-input.v-input--is-focused fieldset {
  border-color: rgba(67, 149, 163, 0.88) !important;
}

::v-deep .enterprise-form .v-label {
  color: #c7d2df !important;
}

::v-deep .enterprise-form input,
::v-deep .enterprise-form textarea {
  color: #f5f8fc !important;
}

::v-deep .choice-radio-group .v-input--radio-group__input {
  display: flex;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
}

::v-deep .choice-radio-group .v-input--selection-controls__input {
  margin-right: 6px;
}

::v-deep .choice-radio {
  margin: 0;
  flex: 0 0 auto;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid #4a4a4a;
  background: #2a2a2a;
  transition: background-color 0.16s ease, border-color 0.16s ease,
    box-shadow 0.16s ease;
}

::v-deep .choice-radio .v-label {
  color: #d6dde6 !important;
  font-size: 12px;
  font-weight: 600;
}

::v-deep .choice-radio.v-item--active {
  border-color: rgba(67, 149, 163, 0.9);
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 2px rgba(67, 149, 163, 0.16);
}

::v-deep .choice-radio.v-item--active .v-label {
  color: #e9f8fc !important;
}

.trust-note {
  margin: 4px 0 0;
  font-size: 12px;
  color: #a9b3bf;
  opacity: 0.86;
}

@media (max-width: 760px) {
  .enterprise-form {
    padding: 14px;
  }

  .choice-block {
    display: block;
    padding: 8px 10px;
  }

  .choice-label {
    margin: 0 0 6px;
    min-width: 0;
  }

  ::v-deep .choice-radio-group .v-input--radio-group__input {
    flex-wrap: wrap;
    overflow-x: visible;
  }
}
</style>
